import { useReferenceStore } from '@/store/reference.store.js';
import { useTenantStore } from '@/store/tenant.store.js';
import {
    getEntityStrategy,
    getEntitySeedSize,
    getEntitiesByStrategy,
    LOAD_STRATEGIES,
} from '@/config/reference.config.js';
import { createIntegrationLogger } from '@/lib/app-log.js';
import { referenceBroadcast, withRefreshLeadership } from '@/lib/services/reference-broadcast.js';
import { shouldApplyHydrateResult } from '@/lib/services/reference-tenant-cache.js';

const integrationLog = createIntegrationLogger('reference-hydration');

const DEBOUNCE_MS = 300;
const DEDUP_WINDOW_MS = 5000;

class HydrationManager {
    constructor() {
        this._sdk = null;
        this._inflightAll = null;
        this._inflightEntities = new Map();
        this._pendingEntityRefresh = new Map();
        this._debounceTimers = new Map();
        this._lastFetched = new Map();
        this._broadcastFn = null;
        this._lastEtag = null;
        /** Bumped on tenant change so in-flight responses for the previous tenant are discarded. */
        this._tenantEpoch = 0;
        /** Epoch of the in-flight force hydrateAll, if any (join instead of parallel force). */
        this._inflightForceEpoch = null;

        this._searchInflight = new Map();
        this._resolveInflight = new Map();
        this._resolveQueue = new Map();
        this._resolveFlushTimers = new Map();
        this._forbiddenEntities = new Set();
    }

    isForbiddenEntity(entity) {
        return this._forbiddenEntities.has(entity);
    }

    init(sdkService) {
        this._sdk = sdkService;
    }

    setBroadcastFn(fn) {
        this._broadcastFn = fn;
    }

    getTenantEpoch() {
        return this._tenantEpoch;
    }

    /**
     * Invalidate in-flight work and caches when the active tenant changes.
     * Outstanding promises may still settle; their results are discarded via epoch.
     */
    resetForTenantChange() {
        this._tenantEpoch += 1;
        this._inflightAll = null;
        this._inflightForceEpoch = null;
        this._inflightEntities.clear();
        this._pendingEntityRefresh.clear();
        this._lastFetched.clear();
        this._lastEtag = null;
        this._searchInflight.clear();
        this._resolveInflight.clear();
        this._resolveQueue.clear();
        this._debounceTimers.forEach((t) => clearTimeout(t));
        this._debounceTimers.clear();
        this._resolveFlushTimers.forEach((t) => clearTimeout(t));
        this._resolveFlushTimers.clear();
        this._forbiddenEntities.clear();
    }

    // ── Bulk hydration (only entities with strategy=bulk) ──────────────

    /**
     * @param {{ force?: boolean, bulkEntities?: string[] }} [opts]
     * @param {string[]} [opts.bulkEntities] — override bulk list (e.g. RBAC-filtered)
     */
    async hydrateAll({ force = false, bulkEntities: bulkEntityList } = {}) {
        if (!this._sdk) return;
        // Soft hydrate may join any in-flight request.
        if (this._inflightAll && !force) return this._inflightAll;
        // Same-epoch force: join instead of starting a parallel request.
        if (
            this._inflightAll
            && force
            && this._inflightForceEpoch != null
            && this._inflightForceEpoch === this._tenantEpoch
        ) {
            return this._inflightAll;
        }
        // Force superseding a soft (or foreign) inflight: bump epoch so the old response is discarded.
        if (force && this._inflightAll && this._inflightForceEpoch == null) {
            this._tenantEpoch += 1;
            this._inflightAll = null;
        }

        const store = useReferenceStore.getState();
        // Soft call while hydrating: join the in-flight request instead of no-op.
        if (store.isHydrating && !force) {
            if (this._inflightAll) return this._inflightAll;
            return;
        }

        const bulkEntities = Array.isArray(bulkEntityList) && bulkEntityList.length > 0
            ? bulkEntityList
            : getEntitiesByStrategy(LOAD_STRATEGIES.BULK);
        if (bulkEntities.length === 0) {
            await this.hydrateSeeds({ force });
            return;
        }

        const epoch = this._tenantEpoch;
        store.setHydrating(true);

        const params = { entities: bulkEntities.join(',') };

        if (!force) {
            const versions = store.versions;
            if (versions && Object.keys(versions).length > 0) {
                params.versions = JSON.stringify(versions);
            }
        }

        const promise = (async () => {
            try {
                const runFetch = async () => {
                    const timer = integrationLog.time('hydrateAll');
                    try {
                        if (!shouldApplyHydrateResult(epoch, this._tenantEpoch)) return;

                        const headers = {};
                        if (this._lastEtag && !force) {
                            headers['If-None-Match'] = this._lastEtag;
                        }

                        const response = await this._sdk.getAll(params, headers);
                        if (!shouldApplyHydrateResult(epoch, this._tenantEpoch)) return;
                        if (!response.success) return;

                        if (response._etag) {
                            this._lastEtag = response._etag;
                        }

                        const { data, versions } = response;
                        if (!data) return;

                        const validData = {};
                        for (const [entity, payload] of Object.entries(data)) {
                            if (payload && payload.items) {
                                validData[entity] = payload;
                            }
                        }

                        if (!shouldApplyHydrateResult(epoch, this._tenantEpoch)) return;

                        if (Object.keys(validData).length > 0) {
                            useReferenceStore.getState().setMultipleEntities(validData, versions || {});
                            this._broadcast('bulk', validData, versions);
                        }
                    } finally {
                        timer.end({ entities: bulkEntities.length });
                    }
                };

                // Force path skips ifAvailable leadership so tenant switch cannot no-op.
                if (force) {
                    await runFetch();
                } else {
                    await withRefreshLeadership('bulk-all', runFetch);
                }
            } catch (err) {
                if (err?.status !== 304 && err?.statusCode !== 304) {
                    integrationLog.error('hydrateAll failed', { error: err?.message || err });
                }
            } finally {
                if (this._inflightAll === promise) {
                    this._inflightAll = null;
                    this._inflightForceEpoch = null;
                }
                if (shouldApplyHydrateResult(epoch, this._tenantEpoch)) {
                    useReferenceStore.getState().setHydrating(false);
                    this.hydrateSeeds({ force });
                }
            }
        })();

        this._inflightAll = promise;
        this._inflightForceEpoch = force ? epoch : null;
        return promise;
    }

    // ── Hybrid seed hydration ───────────────────────────────────────

    async hydrateSeeds({ force = false } = {}) {
        if (!this._sdk) return;

        const hybridEntities = getEntitiesByStrategy(LOAD_STRATEGIES.HYBRID);
        if (hybridEntities.length === 0) return;

        const epoch = this._tenantEpoch;
        const store = useReferenceStore.getState();

        const promises = hybridEntities.map(async (entity) => {
            if (!force && store.seedData[entity]?.length > 0) return;

            try {
                const runSeed = async () => {
                    if (!shouldApplyHydrateResult(epoch, this._tenantEpoch)) return;
                    const seedSize = getEntitySeedSize(entity);
                    const response = await this._sdk.seed({ entity, limit: seedSize });
                    if (!shouldApplyHydrateResult(epoch, this._tenantEpoch)) return;
                    if (!response?.success) return;

                    const items = response.data || [];
                    const version = response.version;

                    useReferenceStore.getState().setSeedData(entity, items, version);
                    this._broadcast('seed', { [entity]: { items, version } }, { [entity]: version });
                };

                if (force) {
                    await runSeed();
                } else {
                    await withRefreshLeadership(`seed:${entity}`, runSeed);
                }
            } catch (err) {
                integrationLog.error(`hydrateSeeds(${entity}) failed`, { error: err?.message || err });
            }
        });

        await Promise.all(promises);
    }

    async refreshSeed(entity, options = {}) {
        if (!this._sdk) return;
        const { force = false } = options;
        const epoch = this._tenantEpoch;

        try {
            const runSeed = async () => {
                if (!shouldApplyHydrateResult(epoch, this._tenantEpoch)) return;
                const seedSize = getEntitySeedSize(entity);
                const response = await this._sdk.seed({ entity, limit: seedSize });
                if (!shouldApplyHydrateResult(epoch, this._tenantEpoch)) return;
                if (!response?.success) return;

                const items = response.data || [];
                const version = response.version;

                useReferenceStore.getState().setSeedData(entity, items, version);
                this._broadcast('seed', { [entity]: { items, version } }, { [entity]: version });
            };
            if (force) {
                await runSeed();
            } else {
                await withRefreshLeadership(`seed:${entity}`, runSeed);
            }
        } catch (err) {
            integrationLog.error(`refreshSeed(${entity}) failed`, { error: err?.message || err });
        }
    }

    // ── Single-entity refresh (bulk + lazy + hybrid) ──────────────────

    refreshEntity(entity, options = {}) {
        const { force = false } = options;
        if (force) {
            integrationLog.debug('refreshEntity forced by invalidation/safety-net', { entity });
        }
        const strategy = getEntityStrategy(entity);
        if (strategy === LOAD_STRATEGIES.RESOLVE_ONLY) {
            return;
        }
        if (strategy === LOAD_STRATEGIES.REMOTE_SEARCH) {
            return;
        }

        if (this._inflightEntities.has(entity)) {
            const pendingForce = this._pendingEntityRefresh.get(entity) || false;
            this._pendingEntityRefresh.set(entity, pendingForce || force);
            integrationLog.debug('refreshEntity queued while inflight', { entity, force, pendingForce: pendingForce || force });
            return;
        }

        if (!force) {
            const now = Date.now();
            const lastFetch = this._lastFetched.get(entity) || 0;
            if (now - lastFetch < DEDUP_WINDOW_MS) return;
        }

        if (this._debounceTimers.has(entity)) {
            clearTimeout(this._debounceTimers.get(entity));
        }

        this._debounceTimers.set(entity, setTimeout(() => {
            this._debounceTimers.delete(entity);
            if (this._inflightEntities.has(entity)) {
                const pendingForce = this._pendingEntityRefresh.get(entity) || false;
                this._pendingEntityRefresh.set(entity, pendingForce || force);
                return;
            }
            const s = getEntityStrategy(entity);
            if (s === LOAD_STRATEGIES.HYBRID) {
                this.refreshSeed(entity, { force });
            } else {
                this._fetchEntity(entity, { force });
            }
        }, DEBOUNCE_MS));
    }

    refreshEntities(entities) {
        entities.forEach(e => this.refreshEntity(e));
    }

    /**
     * Lazy hydration: fetches an entity only if it hasn't been loaded yet.
     * Called automatically by useReferenceSearch / ReferenceLabel for lazy entities.
     */
    async ensureEntity(entity) {
        const store = useReferenceStore.getState();
        if (store.hydratedEntities[entity]) return;
        return this._fetchEntity(entity);
    }

    async _fetchEntity(entity, options = {}) {
        if (!this._sdk) return;
        const { force = false } = options;

        if (this._inflightEntities.has(entity)) {
            return this._inflightEntities.get(entity);
        }

        this._lastFetched.set(entity, Date.now());

        const store = useReferenceStore.getState();
        const currentVersion = store.versions[entity] || 0;
        const epoch = this._tenantEpoch;

        const promise = (async () => {
            try {
                const runFetch = async () => {
                    const timer = integrationLog.time(`fetchEntity:${entity}`);
                    try {
                        if (!shouldApplyHydrateResult(epoch, this._tenantEpoch)) return;
                        const params = { entities: entity };
                        if (!force) {
                            params.versions = JSON.stringify({ [entity]: currentVersion });
                        }
                        const response = await this._sdk.getAll(params);

                        if (!shouldApplyHydrateResult(epoch, this._tenantEpoch)) return;
                        if (!response.success) return;

                        const payload = response.data?.[entity];
                        if (payload && payload.items) {
                            useReferenceStore.getState().setEntityData(entity, payload.items, payload.version);
                            this._broadcast('entity', { [entity]: payload }, response.versions);
                        }
                    } finally {
                        timer.end({ entity });
                    }
                };
                // Force must not silently no-op under ifAvailable Web Locks.
                if (force) {
                    await runFetch();
                } else {
                    await withRefreshLeadership(`entity:${entity}`, runFetch);
                }
            } catch (err) {
                integrationLog.error(`refreshEntity(${entity}) failed`, { error: err?.message || err });
            } finally {
                if (this._inflightEntities.get(entity) === promise) {
                    this._inflightEntities.delete(entity);
                }
                if (
                    shouldApplyHydrateResult(epoch, this._tenantEpoch)
                    && this._pendingEntityRefresh.has(entity)
                ) {
                    const pendingForce = this._pendingEntityRefresh.get(entity) || false;
                    this._pendingEntityRefresh.delete(entity);
                    queueMicrotask(() => {
                        this.refreshEntity(entity, { force: pendingForce });
                    });
                } else {
                    this._pendingEntityRefresh.delete(entity);
                }
            }
        })();

        this._inflightEntities.set(entity, promise);
        return promise;
    }

    // ── Remote search (remoteSearch strategy) ─────────────────────────

    async search(entity, query, limit = 50) {
        if (!this._sdk) return [];

        const cacheKey = `${entity}::${query}::${limit}`;
        if (this._searchInflight.has(cacheKey)) {
            return this._searchInflight.get(cacheKey);
        }

        const epoch = this._tenantEpoch;

        const promise = (async () => {
            try {
                const response = await this._sdk.suggest({ entity, q: query, limit });
                if (!shouldApplyHydrateResult(epoch, this._tenantEpoch)) return [];
                const items = response?.data || [];

                const store = useReferenceStore.getState();
                store.setSearchResults(entity, items);
                store.mergeResolvedItems(entity, items);

                return items;
            } catch (err) {
                integrationLog.error(`search(${entity}) failed`, { error: err?.message || err });
                return [];
            } finally {
                if (this._searchInflight.get(cacheKey) === promise) {
                    this._searchInflight.delete(cacheKey);
                }
            }
        })();

        this._searchInflight.set(cacheKey, promise);
        return promise;
    }

    // ── Resolve by IDs (remoteSearch + resolveOnly) ───────────────────

    async resolveIds(entity, ids) {
        if (!this._sdk || !ids || ids.length === 0) return [];

        const store = useReferenceStore.getState();
        const lookups = store._lookups[entity] || new Map();

        const missing = ids.filter((id) => {
            const key = id?.toString?.() || id;
            return key && !lookups.has(key);
        });

        if (missing.length === 0) return [];

        const resolveKey = `${entity}`;
        const existing = this._resolveQueue.get(resolveKey) || new Set();
        missing.forEach((id) => existing.add(id?.toString?.() || id));
        this._resolveQueue.set(resolveKey, existing);

        if (this._resolveFlushTimers.has(resolveKey)) {
            clearTimeout(this._resolveFlushTimers.get(resolveKey));
        }

        const epoch = this._tenantEpoch;
        const waitersKey = `${resolveKey}::waiters`;
        const waiters = this._resolveInflight.get(waitersKey) || [];
        const waiterPromise = new Promise((resolve) => {
            waiters.push(resolve);
            this._resolveInflight.set(waitersKey, waiters);
        });

        this._resolveFlushTimers.set(resolveKey, setTimeout(async () => {
            this._resolveFlushTimers.delete(resolveKey);

            const queue = this._resolveQueue.get(resolveKey);
            this._resolveQueue.delete(resolveKey);
            const pendingWaiters = this._resolveInflight.get(waitersKey) || [];
            this._resolveInflight.delete(waitersKey);

            const settle = () => {
                pendingWaiters.forEach((w) => {
                    try { w(); } catch { /* ignore */ }
                });
            };

            if (!queue || queue.size === 0) { settle(); return; }
            if (!shouldApplyHydrateResult(epoch, this._tenantEpoch)) { settle(); return; }

            const batchIds = [...queue];

            try {
                const response = await this._sdk.resolve({ entity, ids: batchIds });
                if (!shouldApplyHydrateResult(epoch, this._tenantEpoch)) { settle(); return; }
                const items = response?.data || [];
                if (items.length > 0) {
                    useReferenceStore.getState().mergeResolvedItems(entity, items);
                    referenceBroadcast.send({
                        type: 'resolved',
                        entity,
                        items,
                        tenantId: useTenantStore.getState().selectedTenantId || null,
                        timestamp: Date.now(),
                    });
                }
            } catch (err) {
                const status = err?.response?.status ?? err?.status ?? err?.statusCode;
                if (status === 403) {
                    this._forbiddenEntities.add(entity);
                }
                integrationLog.error(`resolveIds(${entity}) failed`, { error: err?.message || err });
            }
            settle();
        }, 50));

        return waiterPromise;
    }

    // ── Broadcasting ──────────────────────────────────────────────────

    _broadcast(type, data, versions) {
        if (this._broadcastFn) {
            this._broadcastFn({
                type,
                data,
                versions,
                tenantId: useTenantStore.getState().selectedTenantId || null,
                timestamp: Date.now(),
            });
        }
    }

    destroy() {
        this._debounceTimers.forEach(t => clearTimeout(t));
        this._debounceTimers.clear();
        this._inflightEntities.clear();
        this._pendingEntityRefresh.clear();
        this._inflightAll = null;
        this._inflightForceEpoch = null;
        this._searchInflight.clear();
        this._resolveQueue.clear();
        this._resolveFlushTimers.forEach(t => clearTimeout(t));
        this._resolveFlushTimers.clear();
        this._forbiddenEntities.clear();
    }
}

export const hydrationManager = new HydrationManager();
